import { Component, ViewChild, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { RepresentanteAgenteService } from '../services/representanteAgente.service';
import { representanteAegente } from '../interface/representanteAgente.interface';
import { ViewRepresentanteComponent } from '../utils/view-representante-succes';
import { KeycloakService } from 'keycloak-angular';
import Swal from 'sweetalert2';
import { Router } from '@angular/router';
import { CARGOS_EMPRESA } from '../utils/cargos-empresa';

/**
 * Componente encargado de gestionar la edición del usuario representante
 * autenticado mediante Keycloak.
 *
 * Permite:
 * - Obtener los datos del usuario logueado desde Keycloak y el backend.
 * - Mostrar un formulario reactivo con validaciones.
 * - Actualizar la información del representante y reflejar los cambios en el sistema.
 *
 * @author Carlos Eduardo Riaño
 * @date 2025-10-01
 */

@Component({
    selector: 'app-representante-usuario',
    templateUrl: '../representante-agente-vigilado.component.html',
    styleUrls: ['../representante-agente-vigilado.component.css'],
})
export class RepresentanteUsuarioComponent implements OnInit {
    constructor(
        private readonly fb: FormBuilder,
        private readonly raService: RepresentanteAgenteService,
        protected readonly keycloak: KeycloakService,
        private readonly router: Router
    ) {}
    representante: representanteAegente;
    usuarioForm: FormGroup;
    userId: string;
    cargosEmpresa: string[] = CARGOS_EMPRESA;
    @ViewChild('viewRepresentanteSucces', { static: false })
    viewSucces: ViewRepresentanteComponent;
    ngOnInit() {
        this.usuarioForm = this.fb.group({
            username: [
                '',
                [
                    Validators.required,
                    Validators.pattern(/^[a-zA-Z0-9]{3,255}$/),
                ],
            ],
            firstName: [
                '',
                [
                    Validators.required,
                    Validators.pattern(/^[a-zA-ZÀ-Öà-ö\s]+$/),
                ],
            ],
            lastName: [
                '',
                [
                    Validators.required,
                    Validators.pattern(/^[a-zA-ZÀ-Öà-ö\s]+$/),
                ],
            ],
            cargo: ['', [Validators.required]],
            telefono: [
                '',
                [Validators.required, Validators.pattern(/^\d{10}$/)],
            ],
            email: ['', [Validators.required, Validators.email]],
        });
        this.obtenerRepresentante();
    }

    /**
     * Obtiene los datos del representante logueado desde Keycloak y el backend
     * y los asigna al formulario.
     *
     * @returns {Promise<void>}
     */
    async obtenerRepresentante(): Promise<void> {
        try {
            this.representante = (await this.keycloak.loadUserProfile()) as any;
            const representante = await this.raService.getRepresentanteAgente(
                `representante-agente-vigilado/Keycloack/${this.representante.id}`
            );
            this.usuarioForm.patchValue({
                username: representante?.username || '',
                firstName: representante?.nombre || '',
                lastName: representante?.apellido || '',
                cargo: representante?.cargo || '',
                telefono: representante.telefono,
                email: representante?.email || '',
            });
        } catch (error) {
            console.error('Error al obtener el representante:', error);
            Swal.fire(
                'Error',
                'No se pudo obtener la información del representante.',
                'error'
            );
        }
    }

    /**
     * Guarda los cambios del usuario en el backend y actualiza los datos del representante.
     * Si la operación es exitosa, muestra un mensaje y redirige al inicio tras reiniciar sesión.
     *
     * @returns {Promise<void>}
     */
    async guardarUsuario(): Promise<void> {
        try {
            if (this.usuarioForm.valid) {
                const usuario: representanteAegente = {
                    id: this.representante.id,
                    username: this.usuarioForm.get('username').value,
                    firstName: this.usuarioForm.get('firstName').value,
                    lastName: this.usuarioForm.get('lastName').value,
                    email: this.usuarioForm.get('email').value,
                    attributes: {
                        cargo: [this.usuarioForm.get('cargo').value],
                        telefono: [this.usuarioForm.get('telefono').value],
                    },
                };
                await this.raService.putRepresentanteAgente(
                    `usuario/agente-vigilado`,
                    usuario
                );
                Swal.fire(
                    '',
                    'Representante actualizado con éxito, se reiniciará la sesión',
                    'success'
                ).then((result) => {
                    if (result.isConfirmed) {
                        this.router.navigate(['/inicio']);
                    }
                });
            } else {
                this.usuarioForm.markAllAsTouched();
                console.log('Ocurrió un error en el formulario');
            }
        } catch (error) {
            console.error('Error al actualizar el usuario:', error);
        }
    }
}
